import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "../app/AppShell";
import { useMe } from "../app/AuthGate";
import { Button } from "../components/Button";
import { Alert, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Select } from "../components/Field";
import { Chip } from "../components/Status";
import {
  EventKindSelect,
  EventTable,
  LockedTable,
  NaruTable,
  OPS_RANGES,
  OpsSection,
  SessionTable,
  TaskIdFilter,
  UsagePanel,
  IssueStageTable,
  SaveHints,
  StageChart,
  foldIssueStages,
  foldUsage,
  saveHints,
  inRange,
  lockedIssues,
} from "../components/Ops";
import type { OpsEvent, OpsRange, UsageAxis } from "../components/Ops";
import { chatApi } from "../chat/api";
import type { Session } from "../chat/api";
import { useChatDock } from "../chat/dock";
import { authApi, authErrText } from "../lib/auth";
import type { RunnerRow } from "../lib/auth";
import { call, errText, issueActsApi, tasksApi } from "../lib/tasks";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 관측 › 지금(세션 진행, #/session) — t-in-port-obs-ops ══
   pr.b3x.app 「지금」의 다섯 판을 이 화면에 흡수했다(docs/decisions/scope.md). 예전 이 자리의 「없음 ·
   M5 미리보기」 목업은 걷었다 — 나루가 붙은 뒤라 실제 세션 기록이 그 자리를 채운다. 빈 판은 판마다
   **무엇이 없고 다음에 무엇을 하면 되는지** 말한다.

   요청은 판마다 따로다 — 하나가 실패해도 나머지는 그린다. 나루 표는 `/admin/` 문이라 관리자에게만 읽는다. */

/** 사용량을 접는 세션 수 — 서버 상한(`check_limit`)이 1000 이다. 꽉 차면 「더 있을 수 있음」을 말한다. */
const SESS_LIMIT = 1000;

export function SessionPage() {
  const auth = useMe();
  const isAdmin = !auth || auth.me.role === "admin";
  const { openDock } = useChatDock();
  const [range, setRange] = useState<OpsRange>("1d");
  const [axis, setAxis] = useState<UsageAxis>("track");
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [sessErr, setSessErr] = useState<string | null>(null);
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [issueErr, setIssueErr] = useState<string | null>(null);
  const [runners, setRunners] = useState<RunnerRow[] | null>(null);
  const [runErr, setRunErr] = useState<string | null>(null);
  const [liveOnly, setLiveOnly] = useState(false);
  const [sessTask, setSessTask] = useState("");
  const [taskSessions, setTaskSessions] = useState<Session[] | null>(null);
  const [events, setEvents] = useState<OpsEvent[] | null>(null);
  const [evErr, setEvErr] = useState<string | null>(null);
  const [evKind, setEvKind] = useState("");
  const [evTask, setEvTask] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [actErr, setActErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const loadEvents = useCallback((task: string) => {
    setEvents(null);
    setEvErr(null);
    call<OpsEvent[]>(`/events?limit=500${task ? `&task=${encodeURIComponent(task)}` : ""}`).then(setEvents, (e) => setEvErr(errText(e)));
  }, []);

  useEffect(() => {
    setSessions(null);
    chatApi.sessions(undefined, SESS_LIMIT).then(
      (r) => {
        setSessions(r);
        setSessErr(null);
      },
      (e) => setSessErr(errText(e)),
    );
    tasksApi.list().then(
      (r) => {
        setIssues(r);
        setIssueErr(null);
      },
      (e) => setIssueErr(errText(e)),
    );
    if (isAdmin)
      authApi.runners().then(
        (r) => {
          setRunners(r);
          setRunErr(null);
        },
        (e) => setRunErr(authErrText(e)),
      );
  }, [isAdmin, tick]);
  useEffect(() => loadEvents(evTask), [loadEvents, evTask, tick]);
  useEffect(() => {
    if (!sessTask) return setTaskSessions(null);
    setTaskSessions(null);
    chatApi.sessions(sessTask, 200).then(setTaskSessions, (e) => setSessErr(errText(e)));
  }, [sessTask, tick]);

  const facts = useMemo(() => {
    const m = new Map((issues ?? []).map((t) => [t.id, { project: t.projectId ?? null, complexity: t.complexity ?? null }]));
    return (id: string) => m.get(id) ?? null;
  }, [issues]);
  const ranged = useMemo(() => inRange(sessions ?? [], range), [sessions, range]);
  const usage = useMemo(() => foldUsage(ranged, axis, facts), [ranged, axis, facts]);
  /* 1000 줄을 다 받았고 가장 오래된 줄이 아직 기간 안이면, 기간 안에 더 있을 수 있다. */
  const capped = (sessions?.length ?? 0) >= SESS_LIMIT && ranged.length === sessions?.length;
  const perIssue = useMemo(() => foldIssueStages(ranged), [ranged]);
  const hints = useMemo(() => saveHints(ranged, perIssue), [ranged, perIssue]);
  const locked = useMemo(() => lockedIssues(issues ?? []), [issues]);
  const shownEvents = useMemo(() => (events ?? []).filter((e) => !evKind || e.kind === evKind), [events, evKind]);

  async function unlock(id: string) {
    setBusy(id);
    setActErr(null);
    try {
      await issueActsApi.setAssignee(id, null);
      setIssues((xs) => (xs ?? []).map((t) => (t.id === id ? { ...t, assignee: null, assigneeKind: null } : t)));
    } catch (e) {
      setActErr(errText(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <AppShell route="/session">
      <PageHead
        title={tx("nav.session")}
        lead={<p>{tx("ops.lead")}</p>}
      />

      {/* 공통 도구는 머리 오른쪽이 아니라 줄 하나로 — 폰에서 셋이 제목을 밀어 「세.」로 잘렸다. */}
      <div className="b-filterbar">
        <div className="b-filterbar-chips">
          <Select size="md" value={range} aria-label={tx("ops.range")} onChange={(e) => setRange(e.target.value as OpsRange)}>
            {OPS_RANGES.map((r) => (
              <option key={r} value={r}>
                {tx(`ops.ranges.${r}`)}
              </option>
            ))}
          </Select>
          <Button size="md" onClick={() => setTick((n) => n + 1)}>
            {tx("ops.reload")}
          </Button>
        </div>
        <div className="b-filterbar-end">
          <a href="#/board">{tx("ops.toBoard")} →</a>
        </div>
      </div>

      <OpsSection id="usage" title={tx("ops.usage.title")} help={tx("ops.usage.help")}>
        {sessErr ? <Alert tone="error">{tx("ops.loadFailed", { what: tx("ops.usage.title"), err: sessErr })}</Alert> : null}
        {!sessions && !sessErr ? <Loading /> : null}
        {sessions ? (
          <>
            <StageChart rows={ranged} range={range} />
            <UsagePanel rows={usage} axis={axis} onAxis={setAxis} capped={capped} />
          </>
        ) : null}
      </OpsSection>

      {sessions ? (
        <>
          <OpsSection id="stages" title={tx("ops.ana.stagesTitle")} help={tx("ops.ana.stagesHelp")}>
            <IssueStageTable key={range} rows={perIssue} />
          </OpsSection>
          <OpsSection id="save" title={tx("ops.ana.saveTitle")} help={tx("ops.ana.saveHelp")}>
            <SaveHints hints={hints} />
          </OpsSection>
        </>
      ) : null}

      <OpsSection id="locked" title={tx("ops.locked.title")} help={tx("ops.locked.help")} end={issues ? <Chip tone={locked.length ? "warning" : "muted"}>{tx("ops.count", { n: locked.length })}</Chip> : null}>
        {issueErr ? <Alert tone="error">{tx("ops.loadFailed", { what: tx("ops.locked.title"), err: issueErr })}</Alert> : null}
        {actErr ? <Alert tone="error">{actErr}</Alert> : null}
        {!issues && !issueErr ? <Loading /> : null}
        {issues ? <LockedTable rows={locked} busyId={busy} onUnlock={unlock} /> : null}
      </OpsSection>

      <OpsSection
        id="naru"
        title={tx("ops.naru.title")}
        help={tx(isAdmin ? "ops.naru.help" : "ops.naru.adminOnly")}
        end={
          isAdmin ? (
            <>
              <label className="b-check">
                <input type="checkbox" checked={liveOnly} onChange={(e) => setLiveOnly(e.target.checked)} />
                <span>{tx("ops.naru.liveOnly")}</span>
              </label>
              <a href="#/settings?tab=runners">{tx("ops.naru.manage")} →</a>
            </>
          ) : null
        }
      >
        {isAdmin ? (
          <>
            {runErr ? <Alert tone="error">{tx("ops.loadFailed", { what: tx("ops.naru.title"), err: runErr })}</Alert> : null}
            {!runners && !runErr ? <Loading /> : null}
            {runners ? <NaruTable rows={runners} liveOnly={liveOnly} /> : null}
          </>
        ) : null}
      </OpsSection>

      <OpsSection id="sess" title={tx("ops.sess.title")} help={tx("ops.sess.help")} end={<TaskIdFilter value={sessTask} onApply={setSessTask} />}>
        {sessErr ? null : !sessions ? <Loading /> : <SessionTable key={sessTask} rows={sessTask ? taskSessions ?? [] : sessions} onLog={(s) => openDock({ sessionId: s.id, taskId: s.taskId })} />}
      </OpsSection>

      <OpsSection
        id="ev"
        title={tx("ops.ev.title")}
        help={tx("ops.ev.help")}
        end={
          <>
            <EventKindSelect value={evKind} onChange={setEvKind} />
            <TaskIdFilter value={evTask} onApply={setEvTask} />
          </>
        }
      >
        {evErr ? <Alert tone="error">{tx("ops.loadFailed", { what: tx("ops.ev.title"), err: evErr })}</Alert> : null}
        {!events && !evErr ? <Loading /> : null}
        {events ? <EventTable rows={shownEvents} /> : null}
      </OpsSection>
    </AppShell>
  );
}
